import { useTranslations } from 'next-intl';
import React from 'react';
import {
  EntityDrawerSection,
  EntityDrawerSections,
} from '../../../components/entity-drawer';
import { EntityDrawerSectionMarkdown } from '../../../components/entity-drawer/EntityDrawerSectionMarkdown';
import type { InternalEnrichedFinding } from '../../../types';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { DrawerCollapsible } from '../../custom-drawer';
import { AlertCircleIcon, Shield01Icon } from '@terra/ui-icons';
import { Text } from '../../../components/text';
import { MarkdownPreview } from '../../markdown-preview';
import {
  RemediationPlanContent,
  parseRemediationPlan,
} from '../RemediationPlanContent';
import { Skeleton } from '../../../components/skeleton';
import { hasVisibleFindingPrevention } from '../findingPrevention.utils';

type FindingDrawerResolutionTabProps = {
  finding: InternalEnrichedFinding;
  useBoxCollapsibles?: boolean;
  isDetailLoading?: boolean;
  preventionSection?: React.ReactNode;
  hasPreventionContent?: boolean;
};
const resolutionDetailSkeleton = (gap: string | undefined) => (
  <EntityDrawerSections gap={gap}>
    <VStack gap={3} w="full" alignItems="stretch">
      <Skeleton h="100px" w="full" borderRadius="lg" />
      <Skeleton h="88px" w="full" borderRadius="lg" />
    </VStack>
  </EntityDrawerSections>
);

export const FindingDrawerResolutionTab = ({
  finding,
  useBoxCollapsibles = false,
  isDetailLoading = false,
  preventionSection,
  hasPreventionContent = false,
}: FindingDrawerResolutionTabProps) => {
  const t = useTranslations(
    'designSystem.findings.findingDrawer.tabs.resolution',
  );
  const tRemediationPlan = useTranslations(
    'designSystem.findings.remediationPlan',
  );
  const noDataTranslation = t('noData');
  const remediationPlan = parseRemediationPlan(finding.remediationPlan);
  const cannotBuildPlan = remediationPlan?.cannotBuildPlan === true;
  const hasValidPlan = remediationPlan && !cannotBuildPlan;
  const hasVisiblePrevention = hasVisibleFindingPrevention(
    finding.preventionStatus,
  );
  const hasContent =
    Boolean(finding.mitigationPlan) ||
    cannotBuildPlan ||
    Boolean(hasValidPlan) ||
    hasVisiblePrevention ||
    hasPreventionContent;

  if (isDetailLoading) {
    return resolutionDetailSkeleton(useBoxCollapsibles ? '6' : undefined);
  }

  const emptyState = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      w="full"
      h="full"
      py={12}
    >
      <VStack gap={4} alignItems="center" maxW="md">
        <Shield01Icon size={24} color="var(--colors-fg-muted)" />
        <VStack gap={2} alignItems="center">
          <Text fontSize="sm" fontWeight="medium" color="fg.default">
            {t('noDataTitle')}
          </Text>
          <Text fontSize="xs" color="fg.muted" textAlign="center">
            {noDataTranslation}
          </Text>
        </VStack>
      </VStack>
    </Box>
  );

  if (!hasContent) {
    return (
      <EntityDrawerSections gap={useBoxCollapsibles ? '6' : undefined}>
        {emptyState}
      </EntityDrawerSections>
    );
  }

  if (useBoxCollapsibles) {
    return (
      <EntityDrawerSections gap="6">
        {preventionSection}
        {finding.mitigationPlan && (
          <DrawerCollapsible
            variant="box"
            title={t('mitigation')}
            defaultOpen={true}
          >
            <MarkdownPreview source={finding.mitigationPlan} fontSize={14} />
          </DrawerCollapsible>
        )}
        {cannotBuildPlan && (
          <DrawerCollapsible
            variant="box"
            title={t('remediation')}
            defaultOpen={true}
          >
            <HStack gap={2} color="fg.muted">
              <AlertCircleIcon size={16} />
              <Text fontSize="sm">{tRemediationPlan('cannotBuildPlan')}</Text>
            </HStack>
          </DrawerCollapsible>
        )}
        {hasValidPlan && (
          <DrawerCollapsible
            variant="box"
            title={t('remediation')}
            defaultOpen={true}
          >
            <RemediationPlanContent plan={remediationPlan} />
          </DrawerCollapsible>
        )}
      </EntityDrawerSections>
    );
  }

  return (
    <EntityDrawerSections>
      {preventionSection}
      {finding.mitigationPlan && (
        <EntityDrawerSectionMarkdown
          title={t('mitigation')}
          value={finding.mitigationPlan}
        />
      )}
      {cannotBuildPlan && (
        <EntityDrawerSection title={t('remediation')}>
          <HStack gap={2} color="fg.muted">
            <AlertCircleIcon size={16} />
            <Text fontSize="sm">{tRemediationPlan('cannotBuildPlan')}</Text>
          </HStack>
        </EntityDrawerSection>
      )}
      {hasValidPlan && (
        <EntityDrawerSection title={t('remediation')}>
          <RemediationPlanContent plan={remediationPlan} />
        </EntityDrawerSection>
      )}
    </EntityDrawerSections>
  );
};
